/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.14.1
 * @since 1.12.0
 */

import { useCallback } from 'react';

import useTableFooterButtons, { IUseTableFooterButtons } from '../../../components/common/TableFooter/hooks/useTableFooterButtons';
import useTableFooterDisabled from '../../../components/common/TableFooter/hooks/useTableFooterDisabled';
import {
    TableFooterContainerStyled,
    TableFooterLeftContainerStyled,
    TableFooterRightContainerStyled,
} from '../../../components/common/TableFooter/styled/TableFooterStyled';
import TableFooterItem from '../../../components/common/TableFooter/TableFooterItem';
import { TableFooterNavStyled } from '../../../components/Table/styled/TableStyled';
import { ITableButtonTypes } from '../../../components/Table/types/tableFooterButtonsTypes';
import { TableFooterNameType } from '../../../components/Table/types/tableTypes';
import { PageCardFooterStyled } from '../../PageAccountInfo/components/styled/PageCardFooterStyled';
import { EUserType } from '../../PageAuth/constants/authConstants';
import { useGetSubscriberIdMetaQuery } from '../store';
import { TSubscriberIdButtonsEnabled } from '../types';

interface ISubscriberIdFooter {
    subscriberId: string;
    userType: EUserType;
}

const SubscriberIdFooter = ({ subscriberId, userType }: ISubscriberIdFooter) => {

    const {
        accessLevel,
        additionalAccount,
        callDetails,
        corporate,
        inquiryCreate,
        labelEdit,
        limit,
        manageBlock,
        tariffChange,
    } = useTableFooterButtons(TableFooterNameType.subscriberId);

    const {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
    } = useTableFooterDisabled(TableFooterNameType.subscriberId);

    const { buttonsEnabled } = useGetSubscriberIdMetaQuery({ subscriberId, userType }, {
        selectFromResult: ({ data }) => ({
            buttonsEnabled: data?.buttons?.reduce((buttons: TSubscriberIdButtonsEnabled, button: ITableButtonTypes) => {
                buttons[button.buttonName] = true;
                return buttons;
            }, {}) ?? {},
        }),
    });

    const renderBtn = useCallback((item: IUseTableFooterButtons) => 
        buttonsEnabled && 
        buttonsEnabled[item.id] && 
        (
            <TableFooterItem
                {...item}
                isDisabled={isBtnDisabled(item.id)}
                key={item.id}
                tooltipMsg={getFooterButtonTooltipMsg(item.id)}
            />
        ), 
    [isBtnDisabled, getFooterButtonTooltipMsg, buttonsEnabled]);

    return (
        <PageCardFooterStyled>
            <TableFooterNavStyled>
                <TableFooterContainerStyled className="table-footer subscriber-footer">
                    <TableFooterLeftContainerStyled className="table-footer__left-container">
                        {[manageBlock, limit, corporate, labelEdit, callDetails,
                            tariffChange, inquiryCreate, additionalAccount].map(renderBtn)}
                    </TableFooterLeftContainerStyled>
                    <TableFooterRightContainerStyled className="table-footer__right-container">
                        {[accessLevel].map(renderBtn)}
                    </TableFooterRightContainerStyled>
                </TableFooterContainerStyled>
            </TableFooterNavStyled>

        </PageCardFooterStyled>
    );
};

export default SubscriberIdFooter;
